import { supabase } from '../lib/supabase';

// ============================================
// ALERT PREFERENCES SERVICE
// ============================================

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    userId: obj?.user_id,
    alertType: obj?.alert_type,
    enabled: obj?.enabled,
    frequency: obj?.frequency,
    emailEnabled: obj?.email_enabled,
    smsEnabled: obj?.sms_enabled,
    pushEnabled: obj?.push_enabled,
    createdAt: obj?.created_at,
    updatedAt: obj?.updated_at
  };
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  return {
    user_id: obj?.userId,
    alert_type: obj?.alertType,
    enabled: obj?.enabled,
    frequency: obj?.frequency,
    email_enabled: obj?.emailEnabled,
    sms_enabled: obj?.smsEnabled,
    push_enabled: obj?.pushEnabled
  };
};

/**
 * Get all alert preferences for current user
 * @returns {Promise} Promise resolving to alert preferences array
 */
export const getAlertPreferences = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      ?.from('alert_preferences')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.order('alert_type', { ascending: true });

    if (error) throw error;

    // If no preferences exist, return defaults
    if (!data || data?.length === 0) {
      return {
        data: [
          {
            alertType: 'ai_match',
            enabled: true,
            frequency: 'instant',
            emailEnabled: true,
            smsEnabled: false,
            pushEnabled: true
          },
          {
            alertType: 'deal_progress',
            enabled: true,
            frequency: 'instant',
            emailEnabled: true,
            smsEnabled: false,
            pushEnabled: true
          },
          {
            alertType: 'market_value',
            enabled: true,
            frequency: 'daily',
            emailEnabled: true,
            smsEnabled: false,
            pushEnabled: true
          },
          {
            alertType: 'scout_response',
            enabled: true,
            frequency: 'instant',
            emailEnabled: true,
            smsEnabled: false,
            pushEnabled: true
          }
        ],
        error: null
      };
    }

    return { data: data?.map(toCamelCase), error: null };
  } catch (error) {
    console.error('Error fetching alert preferences:', error);
    return { data: null, error: error?.message };
  }
};

/**
 * Update alert preference for specific alert type
 * @param {String} alertType - Alert type (ai_match, deal_progress, market_value, scout_response)
 * @param {Object} preferences - Preference settings
 * @returns {Promise} Promise resolving to updated preference
 */
export const updateAlertPreference = async (alertType, preferences) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const snakeCaseData = {
      user_id: user?.id,
      alert_type: alertType,
      ...toSnakeCase(preferences)
    };

    const { data, error } = await supabase
      ?.from('alert_preferences')
      ?.upsert([snakeCaseData], { onConflict: 'user_id,alert_type' })
      ?.select()
      ?.single();

    if (error) throw error;

    return { data: toCamelCase(data), error: null };
  } catch (error) {
    console.error('Error updating alert preference:', error);
    return { data: null, error: error?.message };
  }
};

/**
 * Batch update multiple alert preferences
 * @param {Array} preferences - Array of preference objects with alertType and settings
 * @returns {Promise} Promise resolving to updated preferences
 */
export const batchUpdateAlertPreferences = async (preferences) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    const snakeCaseData = preferences?.map(pref => ({
      user_id: user?.id,
      alert_type: pref?.alertType,
      enabled: pref?.enabled,
      frequency: pref?.frequency,
      email_enabled: pref?.emailEnabled,
      sms_enabled: pref?.smsEnabled,
      push_enabled: pref?.pushEnabled
    }));

    const { data, error } = await supabase
      ?.from('alert_preferences')
      ?.upsert(snakeCaseData, { onConflict: 'user_id,alert_type' })
      ?.select();

    if (error) throw error;

    return { data: data?.map(toCamelCase), error: null };
  } catch (error) {
    console.error('Error batch updating alert preferences:', error);
    return { data: null, error: error?.message };
  }
};

/**
 * Reset alert preferences to defaults
 * @returns {Promise} Promise resolving to default preferences
 */
export const resetAlertPreferences = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Delete existing preferences
    const { error: deleteError } = await supabase
      ?.from('alert_preferences')
      ?.delete()
      ?.eq('user_id', user?.id);

    if (deleteError) throw deleteError;

    // Return defaults
    return {
      data: [
        {
          alertType: 'ai_match',
          enabled: true,
          frequency: 'instant',
          emailEnabled: true,
          smsEnabled: false,
          pushEnabled: true
        },
        {
          alertType: 'deal_progress',
          enabled: true,
          frequency: 'instant',
          emailEnabled: true,
          smsEnabled: false,
          pushEnabled: true
        },
        {
          alertType: 'market_value',
          enabled: true,
          frequency: 'daily',
          emailEnabled: true,
          smsEnabled: false,
          pushEnabled: true
        },
        {
          alertType: 'scout_response',
          enabled: true,
          frequency: 'instant',
          emailEnabled: true,
          smsEnabled: false,
          pushEnabled: true
        }
      ],
      error: null
    };
  } catch (error) {
    console.error('Error resetting alert preferences:', error);
    return { data: null, error: error?.message };
  }
};

export default {
  getAlertPreferences,
  updateAlertPreference,
  batchUpdateAlertPreferences,
  resetAlertPreferences
};